import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { ACCESS_ROLES } from '../../utils/access';
import { getProjectAccessMembers as getProjectAccessMembersBase } from '../../utils/projects';
import { getUserLabel as getUserLabelBase } from '../../utils/users';
import { loadOwnershipTransfers, loadMemberships, loadCabinetInvites, loadSentCabinetInvites, loadProjectInvites } from '../../services/access';
import { handleCabinetInviteAction as handleCabinetInviteActionBase, handleCancelSentInvite as handleCancelSentInviteBase, handleProjectInviteAction as handleProjectInviteActionBase, handleAddMember as handleAddMemberBase, handleUpdateMember as handleUpdateMemberBase, handleAddProjectMember as handleAddProjectMemberBase, handleUpdateProjectMember as handleUpdateProjectMemberBase } from '../../services/access-actions';
import logger from "../../utils/logger";
export function useSettingsState({
  companyId,
  companyInfo,
  setCompanyInfo,
  marketplaces,
  workspaces,
  companyUsers,
  setActiveSection,
  loadMarketplaces,
  loadWorkspaces,
  settingsTab: externalSettingsTab,
  setSettingsTab: externalSetSettingsTab,
  openProjectModal
}) {
  const {
    request,
    showStatus,
    setStatus
  } = useApi();
  const {
    token
  } = useAuth();
  const [settingsTabState, setSettingsTabState] = useState('cabinets');
  const settingsTab = externalSettingsTab ?? settingsTabState;
  const setSettingsTab = externalSetSettingsTab ?? setSettingsTabState;
  const [accessAccountId, setAccessAccountId] = useState('');
  const [memberships, setMemberships] = useState([]);
  const [accessForm, setAccessForm] = useState({
    email: '',
    role: 'manager'
  });
  const [accessIdentity, setAccessIdentity] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);
  const [projectAccessWorkspaceId, setProjectAccessWorkspaceId] = useState('');
  const [projectAccessMode, setProjectAccessMode] = useState('email');
  const [projectAccessIdentity, setProjectAccessIdentity] = useState('');
  const [projectAccessRole, setProjectAccessRole] = useState('manager');
  const [projectAccessBusy, setProjectAccessBusy] = useState(false);
  const [cabinetInvites, setCabinetInvites] = useState([]);
  const [sentCabinetInvites, setSentCabinetInvites] = useState([]);
  const [projectInvites, setProjectInvites] = useState([]);
  const [ownershipTransfers, setOwnershipTransfers] = useState([]);
  const accessLoadKeyRef = useRef('');
  const accessRoles = ACCESS_ROLES;
  useEffect(() => {
    if (marketplaces.length && !accessAccountId) {
      setAccessAccountId(marketplaces[0].id);
    }
  }, [marketplaces, accessAccountId]);
  useEffect(() => {
    if (settingsTab === 'cabinets' && workspaces.length && !projectAccessWorkspaceId) {
      setProjectAccessWorkspaceId(workspaces[0].id);
    }
  }, [settingsTab, workspaces, projectAccessWorkspaceId]);
  const loadOwnershipTransfersLocal = useCallback(async () => {
    if (!token) return;
    await loadOwnershipTransfers(request, token, setOwnershipTransfers);
  }, [request, token]);
  const loadMembershipsLocal = useCallback(async accountId => {
    if (!token) return;
    await loadMemberships(request, accountId, setMemberships);
  }, [request, token]);
  const refreshMemberships = useCallback(async () => {
    if (!accessAccountId) return;
    await loadMembershipsLocal(accessAccountId);
  }, [accessAccountId, loadMembershipsLocal]);
  const loadCabinetInvitesLocal = useCallback(async () => {
    if (!token) return;
    await loadCabinetInvites(request, setCabinetInvites);
  }, [request, token]);
  const loadSentInvitesLocal = useCallback(async accountId => {
    if (!token) return;
    await loadSentCabinetInvites(request, accountId, setSentCabinetInvites);
  }, [request, token]);
  const loadProjectInvitesLocal = useCallback(async () => {
    if (!token) return;
    await loadProjectInvites(request, setProjectInvites);
  }, [request, token]);
  useEffect(() => {
    if (settingsTab !== 'cabinets') return;
    if (!token) return;
    const accessKey = `${token}|${settingsTab}|${accessAccountId || ''}`;
    if (accessLoadKeyRef.current === accessKey) return;
    accessLoadKeyRef.current = accessKey;
    if (accessAccountId) {
      loadMembershipsLocal(accessAccountId).catch(error => {
        logger.error(error);
      });
      loadSentInvitesLocal(accessAccountId).catch(error => logger.error(error));
    }
    loadOwnershipTransfersLocal().catch(error => logger.error(error));
    loadCabinetInvitesLocal().catch(error => logger.error(error));
    loadProjectInvitesLocal().catch(error => logger.error(error));
  }, [settingsTab, accessAccountId, token, loadMembershipsLocal, loadSentInvitesLocal, loadOwnershipTransfersLocal, loadCabinetInvitesLocal, loadProjectInvitesLocal]);
  const handleCabinetInviteAction = (inviteId, action) => handleCabinetInviteActionBase({
    inviteId,
    action,
    request,
    showStatus,
    loadCabinetInvites: loadCabinetInvitesLocal,
    loadMarketplaces,
    companyId
  });
  const handleProjectInviteAction = (inviteId, action) => handleProjectInviteActionBase({
    inviteId,
    action,
    request,
    showStatus,
    loadProjectInvites: loadProjectInvitesLocal,
    loadWorkspaces
  });
  const handleAddMember = event => handleAddMemberBase({
    event,
    accessAccountId,
    accessIdentity,
    accessForm,
    request,
    setAccessBusy,
    setStatus,
    showStatus,
    setAccessIdentity,
    loadSentInvites: loadSentInvitesLocal
  });
  const handleCancelSentInvite = inviteId => handleCancelSentInviteBase({
    inviteId,
    accountId: accessAccountId,
    request,
    showStatus,
    loadSentInvites: loadSentInvitesLocal
  });
  const handleUpdateMember = (membershipId, role) => handleUpdateMemberBase({
    membershipId,
    role,
    request,
    setAccessBusy,
    setStatus,
    showStatus,
    loadMemberships: loadMembershipsLocal,
    accessAccountId
  });
  const getProjectAccessMembers = workspaceId => getProjectAccessMembersBase(workspaceId, workspaces);
  const handleAddProjectMember = event => handleAddProjectMemberBase({
    event,
    projectAccessWorkspaceId,
    projectAccessIdentity,
    projectAccessMode,
    projectAccessRole,
    companyUsers,
    request,
    setProjectAccessBusy,
    setStatus,
    showStatus,
    setProjectAccessIdentity,
    loadProjectInvites: loadProjectInvitesLocal,
    loadWorkspaces
  });
  const handleUpdateProjectMember = (workspaceId, userId, role) => handleUpdateProjectMemberBase({
    workspaceId,
    userId,
    role,
    request,
    setProjectAccessBusy,
    setStatus,
    showStatus,
    getProjectAccessMembers,
    loadWorkspaces
  });
  const getUserLabel = user => getUserLabelBase(user);
  const value = useMemo(() => ({
    companyId,
    companyInfo,
    setCompanyInfo,
    settingsTab,
    setSettingsTab,
    setActiveSection,
    marketplaces,
    workspaces,
    memberships,
    accessRoles,
    accessAccountId,
    setAccessAccountId,
    accessForm,
    setAccessForm,
    accessIdentity,
    setAccessIdentity,
    accessBusy,
    handleAddMember,
    handleUpdateMember,
    handleCancelSentInvite,
    projectAccessWorkspaceId,
    setProjectAccessWorkspaceId,
    projectAccessMode,
    setProjectAccessMode,
    projectAccessIdentity,
    setProjectAccessIdentity,
    projectAccessRole,
    setProjectAccessRole,
    projectAccessBusy,
    handleAddProjectMember,
    handleUpdateProjectMember,
    getProjectAccessMembers,
    handleCabinetInviteAction,
    handleProjectInviteAction,
    getUserLabel,
    cabinetInvites,
    sentCabinetInvites,
    projectInvites,
    ownershipTransfers,
    refreshMemberships,
    loadOwnershipTransfers: loadOwnershipTransfersLocal,
    companyUsers,
    loadMarketplaces,
    loadWorkspaces,
    openProjectModal
  }), [companyId, companyInfo, setCompanyInfo, settingsTab, setSettingsTab, setActiveSection, marketplaces, workspaces, memberships, accessRoles, accessAccountId, accessForm, accessIdentity, accessBusy, handleCancelSentInvite, projectAccessWorkspaceId, projectAccessMode, projectAccessIdentity, projectAccessRole, projectAccessBusy, cabinetInvites, sentCabinetInvites, projectInvites, ownershipTransfers, refreshMemberships, companyUsers, loadMarketplaces, loadWorkspaces, openProjectModal]);
  return value;
}
